import React from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import InputAdornment from '@material-ui/core/InputAdornment'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked'
import ClearIcon from '@material-ui/icons/Clear'
import FullscreenIcon from '@material-ui/icons/Fullscreen'
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import SwapVertIcon from '@material-ui/icons/SwapVert'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined'
import type { QuoteFile } from 'paintscout'
import MuiTextField from '@material-ui/core/TextField'
import classnames from 'classnames'
import FileViewerButton from './FileViewerButton'
import FileViewerPdfPreview from './FileViewerPdfPreview'
import type { ShowOn } from './visibility-mapping'
import { showOnToVisibility, visibilityToShowOn } from './visibility-mapping'
import { isImageFile, isPdfFile, isVideoFile, getS3VideoUrl } from './file-type-utils'
import ResponsiveVideo from '../FilePreview/ResponsiveVideo'
import Video from '../FilePreview/Video'
import { imageCrossOrigin, retryCorsImageOnError } from '../../util'

const TITLE_MAX_LENGTH = 100
const DESCRIPTION_MAX_LENGTH = 250

export type FileViewerDetailsFile = QuoteFile & { error?: string }

export interface FileViewerDetailsProps {
  file: FileViewerDetailsFile
  onChange: (file: FileViewerDetailsFile) => void
  onEdit?: () => void
  onChangeFile?: () => void
  onDelete?: () => void
  /**
   * Hides description + Show on. Use when the caller only wants title editing
   * (e.g. files attached to notes).
   */
  titleOnly?: boolean
  /**
   * Read-only — disables Edit/Change/Delete buttons and locks Title/Description/Show on.
   */
  viewOnly?: boolean
  /**
   * Compact (mobile) layout — preview is full-bleed, action buttons render
   * below the preview, and PDFs show their control bar inline beneath the page.
   */
  compact?: boolean
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: theme.palette.common.white
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2, 1, 2),
    minHeight: 44
  },
  headerCompact: {
    display: 'none'
  },
  title: {
    ...theme.typography.subtitle1,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.paintscout.palette.brand.Neutral.P30
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(0, 2, 2, 2)
  },
  scrollCompact: {
    padding: 0
  },
  previewWrap: {
    background: theme.paintscout.palette.brand.Neutral.P96,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 320,
    overflow: 'hidden',
    marginBottom: theme.spacing(2)
  },
  previewWrapCompact: {
    minHeight: 240,
    margin: theme.spacing(1.5, 2, 0, 2),
    background: theme.paintscout.palette.brand.Neutral.P96,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.md
  },
  previewImg: {
    maxWidth: '100%',
    maxHeight: 480,
    objectFit: 'contain',
    display: 'block'
  },
  previewImgCompact: {
    width: '100%',
    height: 'auto',
    maxHeight: '50vh',
    objectFit: 'contain',
    display: 'block'
  },
  previewVideo: {
    maxWidth: '100%',
    maxHeight: 480,
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    display: 'block',
    outline: 'none'
  },
  docPreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: theme.paintscout.palette.brand.Neutral.P50,
    padding: theme.spacing(4)
  },
  viewMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(0, 0.5)
  },
  viewMetaCompact: {
    padding: theme.spacing(0, 2, 2, 2)
  },
  viewTitle: {
    ...theme.typography.subtitle1,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.paintscout.palette.brand.Neutral.P30
  },
  viewCaption: {
    ...theme.typography.body2,
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  pdfHost: {
    width: '100%',
    marginBottom: theme.spacing(2)
  },
  pdfHostCompact: {
    width: '100%',
    padding: theme.spacing(1.5, 2, 0, 2),
    boxSizing: 'border-box'
  },
  compactActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1)
  },
  compactActionsRight: {
    display: 'inline-flex',
    gap: theme.spacing(1)
  },
  compactFields: {
    padding: theme.spacing(0, 2)
  },
  field: {
    marginBottom: theme.spacing(2)
  },
  fieldLabel: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P30,
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(0.5),
    display: 'block'
  },
  showOnRow: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  showOnButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  radioOn: {
    color: theme.palette.primary.main
  },
  radioOff: {
    color: theme.paintscout.palette.brand.Neutral.P70
  }
}))

/**
 * Inline video preview. Prefers the already-uploaded original (`file.src`, i.e.
 * Cloudinary's secure_url) so a freshly-uploaded clip plays immediately: the
 * cloudinary-react <Video> player requests on-demand transcodes that aren't ready
 * right after upload, which made the inline player buffer forever (ENG-729). If the
 * browser can't play the original format (e.g. an HEVC .mov on desktop Chrome),
 * fall back to that transcoding player, which serves a browser-compatible rendition.
 * Rendered with key={file.key} so the fallback state resets when the file changes.
 */
function InlineVideoPreview({ file, className }: { file: FileViewerDetailsFile; className?: string }) {
  const [srcFailed, setSrcFailed] = React.useState(false)

  if (!srcFailed && file.src) {
    return (
      <video
        controls
        playsInline
        preload="metadata"
        src={file.src}
        className={className}
        onError={() => {
          if (file.cloudinaryPublicId) setSrcFailed(true)
        }}
      />
    )
  }
  if (file.cloudinaryPublicId) {
    return <Video publicId={file.cloudinaryPublicId} className={className} />
  }
  return file.src ? <video controls playsInline src={file.src} className={className} /> : null
}

function FileViewerDetails(props: FileViewerDetailsProps) {
  const { file, onChange, onEdit, onChangeFile, onDelete, titleOnly, viewOnly, compact } = props
  const classes = useStyles({})

  const isImage = isImageFile(file)
  const isPdf = isPdfFile(file)
  const isVideo = isVideoFile(file)

  const handleExpand = () => {
    // S3 videos live at `src/s3PublicKey`; opening the bare `src` would 404.
    const url = (isVideo && getS3VideoUrl(file)) || file.src
    if (url && /^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }
  // Files sourced from external providers (e.g. Company Cam) point at live remote URLs.
  // The in-app image editor would re-upload as a new file and break the link, so disable Edit.
  const isLiveRemote = !!file.provider
  const canEdit = isImage && onEdit && !viewOnly && !isLiveRemote
  const showOn = visibilityToShowOn(file.visibility)

  const setShowOn = (next: ShowOn) => {
    onChange({ ...file, visibility: showOnToVisibility(next) })
  }

  return (
    <div className={classes.root}>
      {!viewOnly && (
        <div className={classnames(classes.header, compact && classes.headerCompact)}>
          <span className={classes.title}>Preview</span>
          <div className={classes.actions}>
            {canEdit && (
              <FileViewerButton tone="neutral" onClick={onEdit}>
                Edit
              </FileViewerButton>
            )}
            {onChangeFile && (
              <FileViewerButton tone="neutral" onClick={onChangeFile}>
                Change
              </FileViewerButton>
            )}
            {onDelete && (
              <FileViewerButton tone="danger" onClick={onDelete}>
                Delete
              </FileViewerButton>
            )}
            {file.src && (
              <IconButton size="small" onClick={handleExpand} aria-label="Open in new tab">
                <FullscreenIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        </div>
      )}
      <div className={classnames(classes.scroll, compact && classes.scrollCompact)}>
        {isPdf && file.src && compact ? (
          <div className={classes.pdfHostCompact}>
            <FileViewerPdfPreview file={file} layout="mobile" />
          </div>
        ) : isPdf && file.src ? (
          <div className={classes.pdfHost}>
            <FileViewerPdfPreview file={file} layout="desktop" />
          </div>
        ) : (
          <div className={classnames(classes.previewWrap, compact && classes.previewWrapCompact)}>
            {isVideo && (file.s3PublicKey || file.cloudinaryPublicId || file.src) ? (
              file.src && file.s3PublicKey ? (
                <ResponsiveVideo file={file} classes={{ video: classes.previewVideo }} />
              ) : file.src || file.cloudinaryPublicId ? (
                // Prefer the uploaded original (plays immediately), falling back to
                // the cloudinary transcoding player when the browser can't play that
                // format — see InlineVideoPreview (ENG-729). key resets the fallback
                // state when the previewed file changes.
                <InlineVideoPreview key={file.key} file={file} className={classes.previewVideo} />
              ) : null
            ) : isImage && file.src ? (
              <img
                // Keep the cached response CORS-clean so the editor's CORS load
                // of the same src hits a usable cache entry on Safari, and
                // self-heal an already-poisoned entry via the onError retry.
                crossOrigin={imageCrossOrigin(file)}
                src={file.src}
                alt={file.title || ''}
                className={classnames(classes.previewImg, compact && classes.previewImgCompact)}
                onError={retryCorsImageOnError}
              />
            ) : (
              <div className={classes.docPreview}>
                <InsertDriveFileOutlinedIcon style={{ fontSize: 64 }} />
                <div>{file.title || 'File'}</div>
              </div>
            )}
          </div>
        )}

        {compact && (isImage || isPdf || onChangeFile || onDelete) && (
          <div className={classes.compactActions}>
            <div>
              {canEdit && (
                <FileViewerButton tone="neutral" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" style={{ marginRight: 4 }} />
                  Edit
                </FileViewerButton>
              )}
            </div>
            <div className={classes.compactActionsRight}>
              {onChangeFile && !viewOnly && (
                <FileViewerButton tone="neutral" onClick={onChangeFile}>
                  <SwapVertIcon fontSize="small" style={{ marginRight: 4 }} />
                  Change
                </FileViewerButton>
              )}
              {onDelete && !viewOnly && (
                <FileViewerButton tone="danger" onClick={onDelete}>
                  <DeleteOutlinedIcon fontSize="small" style={{ marginRight: 4 }} />
                  Delete
                </FileViewerButton>
              )}
            </div>
          </div>
        )}

        {viewOnly ? (
          (file.title || file.caption) && (
            <div className={classnames(classes.viewMeta, compact && classes.viewMetaCompact)}>
              {file.title && <div className={classes.viewTitle}>{file.title}</div>}
              {file.caption && <div className={classes.viewCaption}>{file.caption}</div>}
            </div>
          )
        ) : (
          <div className={classnames(compact && classes.compactFields)}>
            <div className={classes.field}>
              <label className={classes.fieldLabel}>Title</label>
              <MuiTextField
                variant="outlined"
                size="small"
                fullWidth
                value={file.title || ''}
                onChange={(e) => onChange({ ...file, title: e.target.value })}
                inputProps={{ maxLength: TITLE_MAX_LENGTH }}
                error={(file.title?.length || 0) >= TITLE_MAX_LENGTH}
                helperText={
                  (file.title?.length || 0) >= TITLE_MAX_LENGTH
                    ? `Maximum ${TITLE_MAX_LENGTH} characters reached`
                    : undefined
                }
                InputProps={{
                  endAdornment: file.title ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => onChange({ ...file, title: '' })}
                        aria-label="Clear title"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined
                }}
              />
            </div>

            {!titleOnly && (
              <>
                <div className={classes.field}>
                  <label className={classes.fieldLabel}>Description</label>
                  <MuiTextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={file.caption || ''}
                    onChange={(e) => onChange({ ...file, caption: e.target.value })}
                    inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                    error={(file.caption?.length || 0) >= DESCRIPTION_MAX_LENGTH}
                    helperText={
                      (file.caption?.length || 0) >= DESCRIPTION_MAX_LENGTH
                        ? `Maximum ${DESCRIPTION_MAX_LENGTH} characters reached`
                        : undefined
                    }
                  />
                </div>

                <div className={classes.field}>
                  <label className={classes.fieldLabel}>Show on</label>
                  <div className={classes.showOnRow}>
                    <FileViewerButton
                      tone="neutral"
                      selected={showOn.estimate}
                      onClick={() => setShowOn({ ...showOn, estimate: !showOn.estimate })}
                      className={classes.showOnButton}
                    >
                      {showOn.estimate ? (
                        <RadioButtonCheckedIcon className={classes.radioOn} fontSize="small" />
                      ) : (
                        <RadioButtonUncheckedIcon className={classes.radioOff} fontSize="small" />
                      )}
                      Estimate
                    </FileViewerButton>
                    <FileViewerButton
                      tone="neutral"
                      selected={showOn.workOrder}
                      onClick={() => setShowOn({ ...showOn, workOrder: !showOn.workOrder })}
                      className={classes.showOnButton}
                    >
                      {showOn.workOrder ? (
                        <RadioButtonCheckedIcon className={classes.radioOn} fontSize="small" />
                      ) : (
                        <RadioButtonUncheckedIcon className={classes.radioOff} fontSize="small" />
                      )}
                      Work Order
                    </FileViewerButton>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default FileViewerDetails
